import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertCircle, CheckCircle, Clock, TrendingUp, RefreshCw, FileCheck, Users, Package, CreditCard, ListTodo, UploadCloud, MessageSquare, MailCheck } from 'lucide-react';
import api from '../../utils/api';
import { useTenant } from '../../context/TenantContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const OperatorDashboard = () => {
  const { activeTenant } = useTenant();
  const { isGlobalAdmin } = useAuth();
  const basePath = isGlobalAdmin() ? '/global-admin' : '/operator';
  const [stats, setStats] = useState({
    totalTasks: 0,
    blockedTasks: 0,
    dueSoon: 0,
    completed: 0
  });
  const [loading, setLoading] = useState(true);
  const [resendLoading, setResendLoading] = useState(false);

  useEffect(() => {
    fetchStats();
  }, [activeTenant]);

  const fetchStats = async () => {
    if (!activeTenant) return;
    setLoading(true);
    try {
      const response = await api.get(`/tasks?tenant_id=${activeTenant}`);
      const tasks = response.data || [];
      setStats({
        totalTasks: tasks.length,
        blockedTasks: tasks.filter(t => t.status === 'Pendiente cliente').length,
        dueSoon: tasks.filter(t => {
          const due = new Date(t.due_date);
          const now = new Date();
          const diff = due - now;
          return diff > 0 && diff < 48 * 60 * 60 * 1000;
        }).length,
        completed: tasks.filter(t => t.status === 'Cerrado').length
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerifications = async () => {
    const confirmed = window.confirm('Reenviar verificaciones a todos los usuarios pendientes?');
    if (!confirmed) return;
    try {
      setResendLoading(true);
      const res = await api.post('/auth/resend-verifications');
      toast.success(`Enviados: ${res.data?.sent || 0} · Omitidos: ${res.data?.skipped || 0}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al reenviar verificaciones');
    } finally {
      setResendLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Tareas', value: stats.totalTasks, icon: TrendingUp, color: 'text-primary' },
    { title: 'Bloqueadas', value: stats.blockedTasks, icon: AlertCircle, color: 'text-secondary' },
    { title: 'Vencen Pronto', value: stats.dueSoon, icon: Clock, color: 'text-amber-500' },
    { title: 'Completadas', value: stats.completed, icon: CheckCircle, color: 'text-emerald-500' }
  ];

  const quickActions = [
    { label: 'Ver Tareas', href: `${basePath}/tasks`, icon: ListTodo },
    ...(isGlobalAdmin()
      ? [
          { label: 'Paquetes', href: `${basePath}/packages`, icon: Package },
          { label: 'Usuarios', href: `${basePath}/users`, icon: Users },
          { label: 'Onboarding', href: `${basePath}/onboarding`, icon: FileCheck },
        ]
      : []),
    { label: 'Facturacion', href: `${basePath}/billing`, icon: CreditCard },
    { label: 'Exportaciones Holded', href: `${basePath}/holded-exports`, icon: UploadCloud },
    { label: 'Chat', href: `${basePath}/chat`, icon: MessageSquare },
  ];

  return (
    <div data-testid="operator-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold text-foreground mb-2">Panel de Control</h1>
          <p className="text-muted-foreground">Vista general de operaciones</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isGlobalAdmin() && (
            <Button variant="outline" onClick={handleResendVerifications} className="gap-2" disabled={resendLoading}>
              <MailCheck className="w-4 h-4" />
              {resendLoading ? 'Enviando...' : 'Reenviar verificaciones'}
            </Button>
          )}
          <Button variant="outline" onClick={fetchStats} className="gap-2">
            <RefreshCw className="w-4 h-4" /> Actualizar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="hover:border-primary/50 hover:shadow-md transition-all duration-300">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.title}</CardTitle>
                <Icon className={`w-5 h-5 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-heading font-bold">{loading ? '...' : stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="col-span-full">
          <CardHeader>
            <CardTitle className="font-heading">Acciones Rapidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <a key={action.href} href={action.href} className="p-4 border rounded-lg hover:border-primary hover:shadow-md transition-all text-center">
                    <Icon className="w-6 h-6 mx-auto mb-2 text-primary" />
                    <div className="font-medium">{action.label}</div>
                  </a>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OperatorDashboard;
